import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import PaywallBanner from "../components/PaywallBanner";
import SubmissionForm from "../components/SubmissionForm";
import SubmissionKanban from "../components/SubmissionKanban";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUS_CLASS = {
  draft: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
  open: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100",
  shortlisting: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100",
  filled: "bg-amber-50 text-amber-700 ring-1 ring-amber-100",
  closed: "bg-rose-50 text-rose-700 ring-1 ring-rose-100",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function fmtTjm(m) {
  if (m.tjm_type === "fixed") return `$${m.tjm_min}`;
  return `$${m.tjm_min} – $${m.tjm_max}`;
}

export default function MissionDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user, organization } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [closeReason, setCloseReason] = useState("no_fit");
  const [invited, setInvited] = useState([]);
  const [showSubmit, setShowSubmit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/missions/${id}`);
      setMission(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) setError(t("mission.error_forbidden"));
      else if (status === 404) setError(t("mission.error_not_found"));
      else setError(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const isPoster = useMemo(() => {
    if (!mission || !user) return false;
    if (user.role === "admin") return true;
    return mission.posted_by_org_id === user.organization_id;
  }, [mission, user]);

  const agencyPaywalled = user?.role === "agency" &&
    !(organization?.subscription_status === "active" || organization?.subscription_status === "trialing");

  useEffect(() => {
    if (!isPoster || !mission) return;
    if (mission.visibility !== "invite_only") return;
    (async () => {
      try {
        const { data } = await api.get(`/missions/${mission.id}/invited-agencies`);
        setInvited(data);
      } catch (err) {
        // non-fatal: owner may not have visibility yet, or invite list is empty
        console.warn("invited-agencies fetch failed:", err);
      }
    })();
  }, [isPoster, mission]);

  const cameFromMarketplace = location.pathname.startsWith("/marketplace/");
  const previewMode = isPoster && cameFromMarketplace && new URLSearchParams(location.search).get("preview") === "1";

  const transition = async (new_status, close_reason) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/missions/${mission.id}/status`, {
        new_status,
        close_reason,
      });
      setMission(data);
      toast.success(t("mission.status_updated"));
      setShowClose(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(t("mission.confirm_delete", { title: mission.title }))) return;
    setBusy(true);
    try {
      await api.delete(`/missions/${mission.id}`);
      toast.success(t("mission.deleted_toast"));
      navigate("/missions/mine");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="p-10 text-center text-slate-500">{t("common.loading")}</div>
      </div>
    );
  }
  if (error || !mission) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-2xl px-6 py-16">
          <div className="rounded-2xl border border-rose-200 bg-white p-8 text-center shadow-sm" data-testid="mission-detail-error">
            <p className="text-sm text-rose-700">{error || t("common.error_generic")}</p>
            <Link to={cameFromMarketplace ? "/marketplace" : "/missions/mine"} className="mt-4 inline-block text-sm font-medium text-indigo-700 hover:text-indigo-800">
              ← {t("common.back")}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mission-detail-page">
      <PaywallBanner />
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <Link
          to={cameFromMarketplace ? "/marketplace" : "/missions/mine"}
          className="text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          ← {cameFromMarketplace ? t("marketplace.title") : t("my_missions.title")}
        </Link>

        {previewMode && (
          <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900" data-testid="mission-preview-banner">
            👁 {t("my_missions_extra.preview_badge")}
          </div>
        )}

        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-[280px]">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl" data-testid="mission-title">
                  {mission.title}
                </h1>
                <StatusPill status={mission.status} />
                {mission.visibility === "invite_only" && (
                  <span className="rounded-full bg-fuchsia-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-fuchsia-700 ring-1 ring-fuchsia-100">
                    {t("mission.visibility_invite_only")}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                <Link
                  to={mission.posted_by_role === "agency" ? `/agencies/${mission.posted_by_org_id}` : `/employers/${mission.posted_by_org_id}`}
                  className="font-medium text-indigo-700 hover:text-indigo-800"
                  data-testid="mission-poster-link"
                >
                  {mission.poster.name}
                </Link>
                <span
                  className={
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 " +
                    (mission.posted_by_role === "agency"
                      ? "bg-indigo-50 text-indigo-700 ring-indigo-100"
                      : "bg-emerald-50 text-emerald-700 ring-emerald-100")
                  }
                  data-testid="mission-poster-role-badge"
                >
                  {t(`dashboard.role_badge_${mission.posted_by_role}`)}
                </span>
                <span>·</span>
                <span>{t(`roster.seniority_${mission.seniority}`)}</span>
                <span>·</span>
                <span>{t(`roster.remote_${mission.remote_policy}`)}</span>
                <span>·</span>
                <span>{mission.location_city}, {mission.country}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-semibold text-slate-900" data-testid="mission-tjm">{fmtTjm(mission)}</div>
              <div className="text-xs text-slate-500">/ {t("mission.per_day")}</div>
              <div className="mt-2 text-xs text-slate-600">
                {t("mission.start")} {fmtDate(mission.start_date)}
              </div>
              <div className="text-xs text-slate-600">
                {mission.duration_months} {t("mission.months")}
                {mission.extension_likely && ` · ${t("mission.extension_likely_short")}`}
              </div>
            </div>
          </div>

          {mission.required_skills?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-1.5">
              {mission.required_skills.map((s) => (
                <span key={s} className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">{s}</span>
              ))}
            </div>
          )}

          {mission.end_client_industry && (
            <p className="mt-4 text-xs text-slate-500">
              {t("mission.end_client_industry")}: <span className="font-medium text-slate-700">{mission.end_client_industry}</span>
            </p>
          )}
          {!mission.end_client_industry && mission.mask_end_client && (
            <p className="mt-4 text-xs italic text-slate-500" data-testid="mission-end-client-masked">
              {t("mission.end_client_masked")}
            </p>
          )}

          <div className="mt-6">
            <h3 className="text-sm font-semibold text-slate-700">{t("mission.description_label")}</h3>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-800" data-testid="mission-description">
              {mission.description}
            </p>
          </div>
        </div>

        {/* Owner actions */}
        {isPoster && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="mission-owner-actions">
            <h3 className="text-sm font-semibold text-slate-900">{t("mission.owner_actions")}</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {(mission.status === "draft" || mission.status === "open" || mission.status === "shortlisting") && (
                <Link to={`/missions/${mission.id}/edit`} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="mission-edit-btn">
                  {t("common.edit")}
                </Link>
              )}
              {mission.status === "draft" && (
                <>
                  <button onClick={() => transition("open")} disabled={busy} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50" data-testid="mission-publish-btn">
                    {t("mission.publish")}
                  </button>
                  <button onClick={del} disabled={busy} className="rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50" data-testid="mission-delete-btn">
                    {t("common.delete")}
                  </button>
                </>
              )}
              {mission.status === "open" && (
                <button onClick={() => transition("shortlisting")} disabled={busy} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="mission-shortlist-btn">
                  {t("mission.move_shortlisting")}
                </button>
              )}
              {mission.status === "shortlisting" && (
                <button onClick={() => transition("open")} disabled={busy} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="mission-back-open-btn">
                  {t("mission.back_to_open")}
                </button>
              )}
              {(mission.status === "open" || mission.status === "shortlisting") && (
                <>
                  <button onClick={() => transition("filled", "filled")} disabled={busy} className="rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-sm font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50" data-testid="mission-fill-btn">
                    {t("mission.mark_filled")}
                  </button>
                  <button onClick={() => setShowClose(true)} disabled={busy} className="rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50" data-testid="mission-close-btn">
                    {t("mission.close")}
                  </button>
                </>
              )}
              {mission.status === "closed" && (
                <button onClick={() => transition("open")} disabled={busy} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50" data-testid="mission-reopen-btn">
                  {t("mission.reopen")}
                </button>
              )}
              {mission.status === "filled" && (
                <span className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm text-slate-600" data-testid="mission-terminal-notice">
                  {t("mission.terminal_filled")}
                </span>
              )}
            </div>

            {showClose && (
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="mission-close-picker">
                <p className="text-sm font-medium text-slate-800">{t("mission.close_reason_label")}</p>
                <div className="mt-2 flex gap-2">
                  {["canceled_by_poster", "no_fit"].map((r) => (
                    <button
                      key={r}
                      onClick={() => setCloseReason(r)}
                      className={
                        "rounded-lg px-3 py-1.5 text-xs font-semibold " +
                        (closeReason === r
                          ? "bg-indigo-600 text-white"
                          : "bg-white ring-1 ring-slate-300 text-slate-700 hover:bg-slate-100")
                      }
                      data-testid={`mission-close-reason-${r}`}
                    >
                      {t(`mission.close_reason_${r}`)}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setShowClose(false)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                    {t("common.cancel")}
                  </button>
                  <button onClick={() => transition("closed", closeReason)} className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700" data-testid="mission-close-confirm">
                    {t("mission.close_confirm")}
                  </button>
                </div>
              </div>
            )}

            {mission.visibility === "invite_only" && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-slate-900">{t("mission.invited_agencies_title")}</h4>
                {invited.length === 0 ? (
                  <p className="mt-2 text-xs text-slate-500">{t("mission.invite_none")}</p>
                ) : (
                  <ul className="mt-2 space-y-1.5" data-testid="mission-invited-list">
                    {invited.map((a) => (
                      <li key={a.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                        <span className="font-medium text-slate-900">{a.name}</span>
                        {a.specialties?.length > 0 && (
                          <span className="ml-2 text-xs text-slate-500">{a.specialties.slice(0, 3).join(" · ")}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
              <h4 className="text-sm font-semibold text-slate-700">{t("mission.submissions_title")}</h4>
              <div className="mt-3">
                <SubmissionKanban missionId={mission.id} mission={mission} />
              </div>
            </div>
          </div>
        )}

        {/* Agency (buyer) actions */}
        {!isPoster && user?.role === "agency" && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="mission-agency-actions">
            <h3 className="text-sm font-semibold text-slate-900">{t("mission.agency_next_step")}</h3>
            <p className="mt-1 text-xs text-slate-500">{t("submissions.propose_hint")}</p>
            {agencyPaywalled ? (
              <Link
                to="/settings/subscription"
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
                data-testid="mission-submit-consultant-paywalled"
              >
                <span>🔒</span>{t("paywall.propose_locked")}
              </Link>
            ) : (
              <button
                onClick={() => setShowSubmit(true)}
                disabled={!(mission.status === "open" || mission.status === "shortlisting")}
                className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
                data-testid="mission-submit-consultant"
                title={mission.status === "open" || mission.status === "shortlisting" ? "" : t("submissions.mission_closed")}
              >
                {t("mission.submit_consultant")}
              </button>
            )}
            {showSubmit && (
              <SubmissionForm
                mission={mission}
                onClose={() => setShowSubmit(false)}
                onCreated={() => { setShowSubmit(false); }}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function StatusPill({ status }) {
  const { t } = useTranslation();
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider " +
        (STATUS_CLASS[status] || STATUS_CLASS.draft)
      }
      data-testid={`mission-status-pill-${status}`}
    >
      {t(`mission.status_${status}`)}
    </span>
  );
}
